import axios from "axios";

// Ensure we NEVER use localhost or undefined values
export const getApiBaseUrl = () => {
  // 1. Force Production API if we are clearly on the Production Admin domain
  if (typeof window !== "undefined" &&
    window.location.hostname === 'admin.angelwatchedu.org') {
    return "https://api.angelwatchedu.org/api/";
  }

  // 2. Auto-detect staging environment by hostname
  if (typeof window !== "undefined" &&
    (window.location.hostname === 'stg.admin.angelwatchedu.org' ||
      window.location.hostname.includes('stg.'))) {
    console.log('🔧 Detected staging environment, using staging API');
    return 'https://api.stg.angelwatchedu.org/api/';
  }

  // 3. Check environment variable
  if (process.env.REACT_APP_API_BASE_URL &&
    process.env.REACT_APP_API_BASE_URL.trim() !== '' &&
    !process.env.REACT_APP_API_BASE_URL.includes('localhost') &&
    !process.env.REACT_APP_API_BASE_URL.includes('127.0.0.1')) {
    return process.env.REACT_APP_API_BASE_URL.endsWith('/')
      ? process.env.REACT_APP_API_BASE_URL
      : process.env.REACT_APP_API_BASE_URL + '/';
  }

  // 4. Check window variable (but validate it)
  if (typeof window !== "undefined" && window.__API_BASE_URL__) {
    const windowUrl = String(window.__API_BASE_URL__).trim();
    if (windowUrl &&
      !windowUrl.includes('localhost') &&
      !windowUrl.includes('127.0.0.1') &&
      (windowUrl.startsWith('http://') || windowUrl.startsWith('https://'))) {
      return windowUrl.endsWith('/') ? windowUrl : windowUrl + '/';
    }
  }

  // 5. Always fallback to production API
  return "https://api.angelwatchedu.org/api/";
};

const API_BASE_URL = getApiBaseUrl();

const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (error) {
    return "";
  }
})();

const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 50000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "69420",
  },
});

// Multi-school admins act on one of several schools at a time. Attach the
// currently-selected school as x-school-id so the backend can scope + authorize
// every school endpoint to that school. No-op for all other roles.
instance.interceptors.request.use((config) => {
  try {
    if (localStorage.getItem("userrole") === "multi_school_admin") {
      const activeSchoolId = localStorage.getItem("activeSchoolId");
      if (activeSchoolId) {
        config.headers = config.headers || {};
        config.headers["x-school-id"] = activeSchoolId;
      }
    }
  } catch (e) {
    // localStorage unavailable (SSR/edge) - safe to skip
  }
  return config;
});

export const setToken = (token) => {
  if (token) {
    // Remove 'Bearer ' if it's already there to prevent duplication
    const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
    instance.defaults.headers.common["Authorization"] = `Bearer ${cleanToken}`;
  } else {
    delete instance.defaults.headers.common["Authorization"];
  }
};

// Add response interceptor to handle 401 errors (expired/invalid tokens)
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is expired or invalid
      console.error("Token is not valid or expired");

      // An impersonation token that expires must not cost the operator their own
      // session. The stored entry holds the token they came in with, so restore
      // it and put them back on their dashboard rather than wiping everything and
      // bouncing to the login screen - which is what used to happen, and which
      // reads as "the platform logged me out" rather than "that read-only session
      // timed out". The literal key mirrors IMP_KEY in ImpersonationBanner; it is
      // repeated rather than imported to keep this module free of component
      // imports, since ImpersonationServices already imports this one.
      try {
        const raw = localStorage.getItem('impersonation');
        const imp = raw ? JSON.parse(raw) : null;
        if (imp && imp.previousToken) {
          localStorage.setItem('token', imp.previousToken);
          if (imp.previousRole) localStorage.setItem('userrole', imp.previousRole);
          if (imp.previousUser) localStorage.setItem('user', imp.previousUser);
          localStorage.removeItem('impersonation');
          setToken(imp.previousToken);
          if (!window.location.pathname.startsWith('/admin/admin-dashboard')) {
            window.location.href = '/admin/admin-dashboard?impersonation=expired';
          }
          return Promise.reject(error);
        }
        // No usable original session: fall through and sign out, but do not leave
        // a stale entry behind to draw a banner over whoever logs in next.
        localStorage.removeItem('impersonation');
      } catch (e) { /* fall through to the normal sign-out */ }

      // Clear authentication data
      setToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('userrole');
      localStorage.removeItem('user');
      localStorage.removeItem('isteacher');
      localStorage.removeItem('needsPayment');
      localStorage.removeItem('paymentDetails');
      localStorage.removeItem('managedSchools');
      localStorage.removeItem('activeSchoolId');

      // Redirect to login page (avoid redirect loops)
      if (window.location.pathname !== '/login' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const responseBody = (response) => response.data;

// const requests = {
//   get: (url, body) => instance.get(url, body).then(responseBody),
//   post: (url, body, headers) =>
//     instance.post(url, body, headers).then(responseBody),
//   imagepost: (url, body, headers) =>
//     instance.post(url, body, {headers}).then(responseBody),
//   put: (url, body) => instance.put(url, body).then(responseBody),
// };

// export default requests;

// Function to get user IP
// Function to get Public IPv4 Address
// Silently fails if CORS blocks the request (expected in browser environments)
const getUserIP = async () => {
  try {
    const res = await axios.get("https://api4.ipify.org?format=json", { timeout: 5000 }); // Force IPv4 with 5s timeout
    return res.data.ip;
  } catch (error) {
    // Silently fail - CORS errors are expected when api4.ipify.org doesn't allow cross-origin requests
    // The backend will handle the default IP address (0.0.0.0) if needed
    return "0.0.0.0"; // Default fallback
  }
};
// Modify requests to include IP in the body
const normalizeUrl = (url = "") => (url.startsWith("/") ? url.slice(1) : url);
const isAbsoluteUrl = (url = "") => /^https?:\/\//i.test(url);

const requests = {
  get: async (url, body = {}) => {
    const ipaddress = await getUserIP();
    return instance.get(normalizeUrl(url), { ...body, ipaddress }).then(responseBody);
  },
  post: async (url, body = {}, headers = {}) => {
    const ipaddress = await getUserIP();
    return instance
      .post(normalizeUrl(url), { ...body, ipaddress }, headers)
      .then(responseBody);
  },
  // Special method for promo code that doesn't include IP address
  postWithoutIP: async (url, body = {}, headers = {}) => {
    return instance
      .post(normalizeUrl(url), body, headers)
      .then(responseBody);
  },
  imagepost: async (url, body = {}, headers = {}) => {
    const ipaddress = await getUserIP();
    return instance
      .post(normalizeUrl(url), { ...body, ipaddress }, { headers })
      .then(responseBody);
  },
  put: async (url, body = {}, config = {}) => {
    console.log('[Requests] PUT', url, 'Headers:', config.headers || instance.defaults.headers.common);
    const ipaddress = await getUserIP();
    return instance.put(normalizeUrl(url), { ...body, ipaddress }, config).then(responseBody);
  },
  delete: async (url) => {
    const ipaddress = await getUserIP();
    return instance.delete(normalizeUrl(url), { ipaddress }).then(responseBody);
  },
  getBlob: async (url, params = {}) => {
    const config = {
      responseType: "blob",
    };

    if (params && Object.keys(params).length > 0) {
      config.params = params;
    }

    if (isAbsoluteUrl(url)) {
      if (API_ORIGIN && url.startsWith(API_ORIGIN)) {
        return instance.get(url.replace(API_ORIGIN, "").replace(/^\/+/, ""), config);
      }
      return axios.get(url, {
        ...config,
        headers: instance.defaults.headers.common,
      });
    }

    const ipaddress = await getUserIP();
    config.params = { ...config.params, ipaddress };
    return instance.get(normalizeUrl(url), config);
  },
};

export default requests;
